
import axios from "axios";
import hashjs from 'hash.js'

/**
 * Gera hash local das permissões para validação de integridade
 * Mesmo algoritmo usado no backend (security-hash.js)
 */
function gerarHashLocal(menus, telaInicial, usuarioId) {
  try {
    if (!Array.isArray(menus)) return null
    
    const permissionsData = {
      userId: String(usuarioId || ''),
      telaInicial: telaInicial || null,
      urls: menus
        .filter(m => m && m.URL && m.EXIBIR === 'S')
        .map(m => String(m.URL).trim())
        .sort()
    }
    
    const jsonString = JSON.stringify(permissionsData)
    // Usando hash.js para evitar dependência externa faltante (crypto-js)
    const hash = hashjs.sha256().update(jsonString).digest('hex')
    
    return hash
  } catch (error) {
    console.error('[menu-store] Erro ao gerar hash local:', error)
    return null
  }
}

export default {
  namespaced: true,
  state: {
      lista: [],
      id: null,
      acessoPainelGerencial: false,
      acessoPainelVendedor: false,
      natureza: null,
      userPermissions: [],      // Armazena as permissões do usuário
      telaInicial: null,         // Tela inicial configurada
      permissionsHash: null,     // Hash recebido do backend
      hashTimestamp: null        // Quando foi gerado
  },
  getters: {
    LISTA: state => state.lista,
    userPermissions: state => state.userPermissions,
    telaInicial: state => state.telaInicial,
    permissionsHash: state => state.permissionsHash,
    hashTimestamp: state => state.hashTimestamp,
    acessoPainelGerencial: state => state.acessoPainelGerencial,
    acessoPainelVendedor: state => state.acessoPainelVendedor,
    
    /**
     * Valida integridade do hash
     * Retorna true se hash válido e não expirado
     */
    isHashValido: state => {
      if (!state.permissionsHash || !state.hashTimestamp) {
        return false
      }
      
      // Verifica expiração (30 minutos)
      const TEMPO_EXPIRACAO = 30 * 60 * 1000 // 30min em ms
      const agora = Date.now()
      const idade = agora - state.hashTimestamp
      
      if (idade > TEMPO_EXPIRACAO) {
        console.warn('[menu-store] Hash expirado (idade:', Math.round(idade/60000), 'min). Recarregando...')
        return false
      }
      
      // Recalcula hash local para comparação
      const usuarioId = window.localStorage.getItem('usuarioId')
      const hashLocal = gerarHashLocal(state.lista, state.telaInicial, usuarioId)
      
      if (!hashLocal) {
        console.error('[SECURITY] Falha ao calcular hash local')
        return false
      }
      
      if (hashLocal !== state.permissionsHash) {
        console.error('[SECURITY] Hash inválido! Possível manipulação detectada.')
        console.error('[SECURITY] Hash esperado:', state.permissionsHash.substring(0, 16) + '...')
        console.error('[SECURITY] Hash calculado:', hashLocal.substring(0, 16) + '...')
        return false
      }
      
      return true
    }
  },
  
  /** MUTACOES SAO SINCRONAS - SEMELHANTES AO COMPUTED */ 
  mutations: {
      SET_LISTA: (state, payload) => {
          state.lista = payload
      },
      SET_TELA_INICIAL: (state, payload) => {
        state.telaInicial = payload
      },
      SET_PERMISSIONS_HASH: (state, payload) => {
        state.permissionsHash = payload
      },
      SET_HASH_TIMESTAMP: (state, payload) => {
        state.hashTimestamp = payload
      },
      setUserPermissions(state, permissions) {
        state.userPermissions = permissions
      },
      setAcessoPainelGerencial(state, payload) {
        state.acessoPainelGerencial = payload
      },
      setAcessoPainelVendedor(state, payload) {
        state.acessoPainelVendedor = payload
      },
      clearUserPermissions(state) {
        state.userPermissions = []
        state.permissionsHash = null
        state.hashTimestamp = null
        state.telaInicial = null
        state.lista = []
      }
  },
  
  /** ACTOINS SAO ASSINCRONOS */
  actions: {
    GERAR_LISTA: ({ commit, getters }) => {
        return new Promise((resolve, reject) => {
          const login = window.localStorage.getItem('login') || 'ninguem'
          let uri = `api/protected/menu/listarporusuario?usuarioid=${login}`
          
          axios.get(uri)
            .then(({ data, status }) => {
              console.log('[menu-store] Resposta do backend (tipo):', typeof data, 'isArray:', Array.isArray(data))
              
              // Validação robusta do formato de dados
              let menus = []
              let telaInicial = null
              let permissionsHash = null
              let hashTimestamp = Date.now()
              
              if (!data) {
                console.warn('[menu-store] Resposta vazia do backend')
              } else if (Array.isArray(data)) {
                // Formato antigo: array direto
                console.log('[menu-store] Formato antigo detectado (array direto)')
                menus = data
              } else if (typeof data === 'object') {
                // Formato novo: objeto com menus, telaInicial, hash
                if (Array.isArray(data.menus)) {
                  console.log('[menu-store] Formato novo detectado (objeto com menus)')
                  menus = data.menus
                  telaInicial = data.telaInicial || null
                  permissionsHash = data.permissionsHash || null
                  hashTimestamp = data.hashTimestamp || Date.now()
                } else {
                  console.error('[menu-store] Formato inválido - data.menus não é array:', data)
                  console.error('[menu-store] Propriedades do objeto:', Object.keys(data))
                }
              } else {
                console.error('[menu-store] Formato de resposta desconhecido:', typeof data, data)
              }
              
              // Garante que menus é sempre array
              if (!Array.isArray(menus)) {
                console.error('[menu-store] menus não é array, usando array vazio')
                menus = []
              }
              
              // Processa permissões
              let userPermissions = []
              let temAcessoGerencial = false
              let temAcessoVendedor = false

              menus.forEach(item => {
                if (item && item.URL) {
                  userPermissions.push(item.URL)
                  if (item.URL === '/dashboard/painelgerencial') temAcessoGerencial = true
                  if (item.URL === '/dashboard/vendedor') temAcessoVendedor = true
                }
              })

              // Commits
              commit('setAcessoPainelGerencial', temAcessoGerencial)
              commit('setAcessoPainelVendedor', temAcessoVendedor)
              commit('setUserPermissions', userPermissions)
              commit('SET_LISTA', menus)
              commit('SET_TELA_INICIAL', telaInicial)
              commit('SET_PERMISSIONS_HASH', permissionsHash)
              commit('SET_HASH_TIMESTAMP', hashTimestamp)

              console.log('[menu-store] Menus carregados:', menus.length, 'Tela inicial:', telaInicial || 'padrão')

              resolve({ data: menus })
            })
            .catch(error => {
              console.error('[menu-store] GERAR_LISTA: failed', error)
              console.error('[menu-store] Error details:', {
                message: error.message,
                response: error.response ? {
                  status: error.response.status,
                  data: error.response.data
                } : 'No response',
                stack: error.stack
              })
              reject(error)
            })
        })
      }
  }
}


// WEBPACK FOOTER //
// ./src/vuex/modulos/menu-store.js